// src/components/pages/mosque/MosqueDetailsPage.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { getMosqueById } from "../../../services/mosqueService";
import type { MosqueDetailDto } from "../../../types/mosque";
import { GoogleMapsLoader } from "../../maps/GoogleMapsLoader";
type NavState = { fromList?: boolean } | null;

// ✅ Local state type: keep everything from MosqueDetailDto, but allow string|boolean|null
// for the women-section fields to match the API ("yes"/"no" or boolean).
type DetailState = Omit<MosqueDetailDto, "hasWomenSection"> & {
  hasWomenSection?: string | boolean | null;
  hasWomenPrayerRoom?: string | boolean | null;
};

// ✅ Arabic Yes/No helper (unchanged logic)
const yesNoAr = (v?: string | boolean | null) => {
  if (v === true) return "نعم";
  if (v === false) return "لا";
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return "—";
  return ["1","true","t","yes","y","نعم","ok","✓"].includes(s) ? "نعم" : "لا";
};

export default function MosqueDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as NavState) || null;

  // ⬇️ Use the local DetailState to avoid TS mismatch
  const [data, setData] = useState<DetailState | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  /* ------------------------------ fetch details ------------------------------ */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true); setErr(null);
        const d = await getMosqueById(Number(id));
        // ⬇️ Normalize only the women-section fields; do NOT change anything else.
        const womenRaw =
          (d as any).hasWomenPrayerRoom ??
          (d as any).hasWomenSection ??
          null;

        const normalized: DetailState = {
          ...d,
          hasWomenSection: womenRaw,
          hasWomenPrayerRoom: (d as any).hasWomenPrayerRoom ?? null,
        };

        if (alive) setData(normalized);
      } catch (e: any) {
        if (alive) setErr(e?.message ?? String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  /* --------------------------------- map init -------------------------------- */
  const mapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!data?.latitude || !data?.longitude) return;
    let map: google.maps.Map | null = null;
    GoogleMapsLoader.load("ar", "SA")
      .then((google) => {
        if (!mapRef.current) return;
        const center = { lat: Number(data.latitude), lng: Number(data.longitude) };
        map = new google.maps.Map(mapRef.current, {
          center, zoom: 14,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
          zoomControl: true,
        });
        new google.maps.Marker({ position: center, map });
      })
      .catch(() => {});
    return () => { map = null; };
  }, [data?.latitude, data?.longitude]);

  /* ------------------------------- breadcrumb ------------------------------- */
  const breadcrumb = useMemo(() => ([
    { label: "الرئيسية", to: "/" },
    { label: "المساجد", to: "/mosquesearch" },
    { label: "تفاصيل المسجد", to: null }
  ]), []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6" dir="rtl">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 p-6">
          <p className="text-neutral-700 dark:text-neutral-200">جاري التحميل…</p>
        </div>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6" dir="rtl">
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-6">
          <div className="mb-4 text-red-700 dark:text-red-300">{err || "المسجد غير موجود"}</div>
          {/* ✅ DGA button (green) aligned to end */}
          <div className="flex justify-end">
            <button
              className="inline-flex items-center rounded-xl border border-neutral-300 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
              onClick={() => navigate("/mosquesearch")}
            >
              رجوع
            </button>
          </div>
        </div>
      </div>
    );
  }

  const mapsUrl =
    data.latitude && data.longitude
      ? `https://www.google.com/maps?q=${encodeURIComponent(`${data.latitude},${data.longitude}`)}&hl=ar&z=16`
      : "";

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6" dir="rtl">
      {/* ------------------------------- Breadcrumb ------------------------------ */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
          {breadcrumb.map((b, i) => {
            const isLast = !b.to;
            return (
              <li key={i} className="flex items-center">
                {b.to ? (
                  <Link
                    to={b.to}
                    className="hover:text-sa-700 hover:underline"
                  >
                    {b.label}
                  </Link>
                ) : (
                  <span className="font-medium text-neutral-800 dark:text-neutral-100">{b.label}</span>
                )}
                {!isLast && <span className="mx-2 text-neutral-400">/</span>}
              </li>
            );
          })}
        </ol>
      </nav>

      {/* -------------------------------- Actions -------------------------------- */}
      <div className="mb-4 flex justify-end">
        {/* ✅ DGA green button identical to LandDetail style */}
<button
        className="inline-flex h-11 items-center justify-center rounded-xl bg-sa-600 px-5 text-white text-base font-[600] hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2"
        onClick={() => navigate("/mosquesearch")}
      >
        رجوع
      </button>
      </div>

      {/* ------------------------------- Main Layout ------------------------------ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ----------------------------- LEFT: Info card ---------------------------- */}
        <section className="rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          <header className="border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 text-center ">
              معلومات المسجد
            </h2>
          </header>
          <div className="p-5">
            {/* ✅ DGA definition list pattern (same rhythm as LandDetail) */}
            <dl className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-6 gap-y-3">
              <dt className="text-neutral-500 dark:text-neutral-400">اسم المسجد</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{data.name || "—"}</dd>

              <dt className="text-neutral-500 dark:text-neutral-400">الرقم التسلسلي للمسجد</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{data.number || "—"}</dd>

              <dt className="text-neutral-500 dark:text-neutral-400">التصنيف</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{data.classificationName || data.mosqueClassification || "—"}</dd>

              <dt className="text-neutral-500 dark:text-neutral-400">المنطقة</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{data.regionName || "—"}</dd>

              <dt className="text-neutral-500 dark:text-neutral-400">المدينة</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{data.cityName || "—"}</dd>

              <dt className="text-neutral-500 dark:text-neutral-400">المركز</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{data.centerName || "—"}</dd>

              <dt className="text-neutral-500 dark:text-neutral-400">الحي</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{data.districtName || "—"}</dd>

              <dt className="text-neutral-500 dark:text-neutral-400">العنوان الكامل</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{data.fullAddress || "—"}</dd>

              <dt className="text-neutral-500 dark:text-neutral-400">قسم النساء</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{yesNoAr(data.hasWomenSection ?? data.hasWomenPrayerRoom)}</dd>

              <dt className="text-neutral-500 dark:text-neutral-400">الحالة</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{data.isActive ? "نشط" : "غير نشط"}</dd>
            </dl>

            {/* Divider with DGA density */}
            <div className="my-4 h-px bg-neutral-200 dark:bg-neutral-800" />

            {/* ✅ Google Maps link button (full width) */}
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-sa-600 px-5 text-white text-md font-[600] hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2"
              >
                موقع المسجد في قوقل ماب
              </a>
            )}

            {/* أزرار الإبلاغ والتقييم — المتبرع أو من أنشأ حساباً (مقيم/شريك) يمكنه رفع بلاغ أو تقييم */}
            <div className="mt-3 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate(`/mosques/${id}/report`)}
                className="inline-flex h-11 flex-1 min-w-[140px] items-center justify-center rounded-xl border border-neutral-300 bg-white px-4 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2"
              >
                الإبلاغ عن مخالفة
              </button>
              <button
                type="button"
                onClick={() => navigate(`/mosques/${id}/evaluate`)}
                className="inline-flex h-11 flex-1 min-w-[140px] items-center justify-center rounded-xl border border-neutral-300 bg-white px-4 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2"
              >
                تقييم حالة المسجد
              </button>
            </div>
          </div>
        </section>

        {/* ------------------------- RIGHT: Map + QR stacked ------------------------ */}
        <div className="space-y-6">
          {/* Map card */}
          <section className="rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
            <header className="border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
              <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 text-center">
                الموقع الجغرافي
              </h2>
            </header>
            <div className="p-5">
              {/* Height similar to LandDetail map block */}
              <div ref={mapRef} className="h-72 w-full rounded-xl border border-neutral-200 dark:border-neutral-800" />
            </div>
          </section>

          {/* QR card */}
          <section className="rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
            <header className="border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
              <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 text-center">
                باركود موقع المسجد
              </h2>
            </header>
            <div className="p-5">
              <div className="flex items-center justify-center">
                {mapsUrl ? (
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="block">
                    <img
                      alt="QR"
                      width={140}
                      height={140}
                      className="rounded-xl border border-neutral-200 dark:border-neutral-800"
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(mapsUrl)}`}
                    />
                  </a>
                ) : (
                  <div className="flex h-[140px] w-[140px] items-center justify-center rounded-xl border border-dashed border-neutral-300 text-neutral-400 dark:border-neutral-700 dark:text-neutral-500">
                    QR
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Optional note to keep parity with list-navigation logic */}
      {navState?.fromList ? (
        <div className="sr-only">وصلت من صفحة القائمة</div>
      ) : null}
    </div>
  );
}
